import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import { normalizeRichText, richTextIsEmpty } from '../../../components/RichContent';
import { WarningIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import AdminModal from '../../components/AdminModal';
import SharedEndorsementModal from '../../components/endorsements/EndorsementModal';
import { CONTENT_ORIGINS } from '../../../constants/ballotInitiatives';
import {
  PROFILE_FIELDS, PROFILE_FIELD_LABELS, COVERAGE_LABELS, RESPONSE_STATUS_LABELS, STANCE_LABELS, POSITION_SOURCE_LABELS,
} from '../../../constants/candidateProfiles';
import type { CoverageTier, ProfileField, ResponseStatus, Stance } from '../../../constants/candidateProfiles';
import { apiError, labelClasses } from '../elections/InitiativeDetailPage';
import {
  deleteProfileRow, fetchMeasureOptions, saveProfile, saveProfileRow,
  type CandidateEndorsement, type CandidatePosition, type CandidateProfile, type CandidateStatement,
  type ProfilePatch, type ProfileRowKind,
} from '../../lib/candidateProfiles';

/**
 * Hand editors for a candidate profile (CANDIDATE_PROFILES_PRD.md v1.7, O1
 * revisited) — the correction path beside the bundle import. The server holds
 * every rule (the importer's per-row checks, the contest tier, the declared
 * origin); these forms pre-check only what they can say more helpfully, and
 * show the server's message as-is otherwise.
 */

const STATEMENT_SOURCE_LABELS: Record<string, string> = {
  questionnaire: 'Questionnaire',
  website: 'Campaign site',
  announcement: 'Announcement',
  debate: 'Debate',
  other: 'Other',
};

const hint = 'mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary';
const fieldGrid = 'grid grid-cols-1 gap-4 sm:grid-cols-2';
const blank = (v: string) => (v.trim() === '' ? null : v.trim());
const dateValue = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '');
const today = () => new Date().toISOString().slice(0, 10);

const ErrorNote: React.FC<{ error: string | null }> = ({ error }) =>
  error ? <p role="alert" className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p> : null;

const ReimportNote: React.FC = () => (
  <p className="flex items-start gap-2 rounded-lg bg-warning-100 px-3 py-2 text-xs text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
    <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" />
    <span>
      A later bundle import replaces these rows with the bundle&apos;s. The import preview warns first — carry the
      correction into the skill&apos;s next version so the record PDF agrees.
    </span>
  </p>
);

interface SaveFooterProps {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  disabled?: boolean;
  label?: string;
}
const SaveFooter: React.FC<SaveFooterProps> = ({ onCancel, onSave, saving, disabled, label = 'Save' }) => (
  <>
    <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
    <Button variant="primary" size="sm" loading={saving} disabled={disabled || saving} onClick={onSave}>{label}</Button>
  </>
);

// ── The six fields + metadata ────────────────────────────────────────────────

interface ProfileEditModalProps {
  candidateId: string;
  isOpen: boolean;
  profile: CandidateProfile | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Edit (or start) the profile: tier, facts-as-of, version, and the six fields each with a declared origin. */
export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ candidateId, isOpen, profile, onClose, onSaved }) => {
  const [tier, setTier] = useState<CoverageTier>('standard');
  const [tierTouched, setTierTouched] = useState(false);
  const [factsAsOf, setFactsAsOf] = useState('');
  const [version, setVersion] = useState('');
  const [text, setText] = useState<Record<string, string>>({});
  const [origins, setOrigins] = useState<Record<string, string>>({});
  const [details, setDetails] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTier(profile?.coverage_tier ?? 'standard');
    setTierTouched(false);
    setFactsAsOf(dateValue(profile?.facts_as_of) || (profile ? '' : today()));
    setVersion(profile?.profile_version ?? '');
    const nextText: Record<string, string> = {};
    const nextOrigins: Record<string, string> = {};
    const nextDetails: Record<string, string> = {};
    for (const field of PROFILE_FIELDS) {
      nextText[field] = profile?.[field] ?? '';
      const entry = profile?.content_provenance?.[field];
      // No default: an undeclared field opens with nothing chosen, as on measures.
      nextOrigins[field] = entry?.origin ?? '';
      nextDetails[field] = entry?.tool ?? entry?.source ?? '';
    }
    setText(nextText);
    setOrigins(nextOrigins);
    setDetails(nextDetails);
    setError(null);
  }, [isOpen, profile]);

  const undeclared = PROFILE_FIELDS.filter((f) => !richTextIsEmpty(text[f]) && !origins[f]);

  const save = useMutation({
    mutationFn: () => {
      const patch: ProfilePatch = { facts_as_of: factsAsOf || null, profile_version: blank(version) };
      // Tier is per contest: only send it when the admin changed it, so an
      // unrelated edit never trips the contest check and a new profile takes
      // its race's tier from the server.
      if (tierTouched && tier !== profile?.coverage_tier) patch.coverage_tier = tier;
      const provenance: NonNullable<ProfilePatch['content_provenance']> = {};
      for (const field of PROFILE_FIELDS) {
        const value = normalizeRichText(text[field]);
        patch[field] = value || null;
        if (!value) { provenance[field] = null; continue; }
        const origin = origins[field] as 'staff_written' | 'ai_reviewed' | 'official_source';
        const entry: { origin: typeof origin; tool?: string; source?: string } = { origin };
        if (origin === 'ai_reviewed' && details[field]?.trim()) entry.tool = details[field].trim();
        if (origin === 'official_source' && details[field]?.trim()) entry.source = details[field].trim();
        provenance[field] = entry;
      }
      patch.content_provenance = provenance;
      return saveProfile(candidateId, patch);
    },
    onSuccess: () => {
      toast.success(profile ? 'Profile saved' : 'Profile started — draft, not visible to citizens');
      onSaved();
      onClose();
    },
    onError: (err: any) => setError(apiError(err, 'Failed to save the profile')),
  });

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title={profile ? 'Edit citizen profile' : 'Start a citizen profile'}
      subtitle={profile?.publication_status === 'published' ? 'This profile is published — changes are live for citizens as soon as you save.' : 'Draft — nothing here is visible to citizens until the profile is published.'}
      size="lg"
      footer={<SaveFooter onCancel={onClose} onSave={() => save.mutate()} saving={save.isPending} disabled={undeclared.length > 0} />}
    >
      <div className="space-y-6">
        <ErrorNote error={error} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="profile-tier" className={labelClasses}>Coverage tier</label>
            <select id="profile-tier" className="input" value={tier} onChange={(e) => { setTier(e.target.value as CoverageTier); setTierTouched(true); }}>
              <option value="standard">{COVERAGE_LABELS.standard}</option>
              <option value="full">{COVERAGE_LABELS.full}</option>
            </select>
            <p className={hint}>Every candidate in a contest carries the same tier{profile ? '' : ' — left alone, a new profile takes its race’s tier'}.</p>
          </div>
          <div>
            <label htmlFor="profile-facts" className={labelClasses}>Facts as of</label>
            <input id="profile-facts" type="date" className="input" value={factsAsOf} onChange={(e) => setFactsAsOf(e.target.value)} />
          </div>
          <div>
            <label htmlFor="profile-version" className={labelClasses}>Version</label>
            <input id="profile-version" type="text" maxLength={32} className="input" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. v1.1" />
          </div>
        </div>

        <p className="flex items-start gap-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          <HelpTrigger contentKey="candidate-profile-editing" size="sm" ariaLabel="Help for editing a candidate profile" />
          <span>The origin you choose becomes a public label — an AI-assisted or official-source field is marked as such
          wherever citizens read it, and an unlabelled field is a claim that our team wrote it.</span>
        </p>
        {undeclared.length > 0 && (
          <p className="rounded-lg bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
            Choose where the text came from for: {undeclared.map((f) => PROFILE_FIELD_LABELS[f]).join(', ')}.
          </p>
        )}

        {PROFILE_FIELDS.map((field: ProfileField) => {
          const filled = !richTextIsEmpty(text[field]);
          const origin = origins[field];
          return (
            <div key={field} className="border-b border-border-light pb-5 last:border-b-0 dark:border-border-dark">
              <span className={labelClasses}>{PROFILE_FIELD_LABELS[field]}</span>
              <QuillEditor value={text[field] ?? ''} onChange={(value) => setText((c) => ({ ...c, [field]: value }))} />
              {filled && (
                <fieldset className="mt-3">
                  <legend className="mb-1.5 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    Where did this text come from? *
                  </legend>
                  <div className="space-y-1.5">
                    {CONTENT_ORIGINS.map((option) => (
                      <label key={option.value} className="flex items-start gap-2 text-sm">
                        <input
                          type="radio"
                          name={`profile-origin-${field}`}
                          value={option.value}
                          checked={origin === option.value}
                          onChange={() => setOrigins((c) => ({ ...c, [field]: option.value }))}
                          className="mt-1"
                        />
                        <span>
                          <span className="text-text-primary dark:text-text-dark-primary">{option.label}</span>
                          <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">{option.hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {(origin === 'ai_reviewed' || origin === 'official_source') && (
                    <div className="mt-2">
                      <label htmlFor={`profile-detail-${field}`} className={labelClasses}>
                        {origin === 'ai_reviewed' ? 'Tool used' : 'Source'}
                      </label>
                      <input
                        id={`profile-detail-${field}`}
                        type="text"
                        maxLength={120}
                        className="input"
                        value={details[field] ?? ''}
                        onChange={(e) => setDetails((c) => ({ ...c, [field]: e.target.value }))}
                        placeholder={origin === 'ai_reviewed' ? 'e.g. Claude Opus 5' : 'e.g. SOS candidate filing'}
                      />
                    </div>
                  )}
                </fieldset>
              )}
            </div>
          );
        })}
      </div>
    </AdminModal>
  );
};

// ── Rows ─────────────────────────────────────────────────────────────────────

interface RowModalProps<Row> {
  candidateId: string;
  isOpen: boolean;
  /** null = add a new row */
  row: Row | null;
  onClose: () => void;
  onSaved: () => void;
}

function useRowSave(candidateId: string, kind: ProfileRowKind, rowId: string | null, onSaved: () => void, onClose: () => void, noun: string) {
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (input: Parameters<typeof saveProfileRow>[3]) => saveProfileRow(candidateId, kind, rowId, input),
    onSuccess: () => {
      toast.success(rowId ? `${noun} updated` : `${noun} added`);
      onSaved();
      onClose();
    },
    onError: (err: any) => setError(apiError(err, `Failed to save the ${noun.toLowerCase()}`)),
  });
  return { mutation, error, setError };
}

export const StatementModal: React.FC<RowModalProps<CandidateStatement> & { nextOrder: number }> = ({
  candidateId, isOpen, row, onClose, onSaved, nextOrder,
}) => {
  const [form, setForm] = useState({
    source_type: 'questionnaire', source_name: '', source_url: '', source_date: '', question: '', body: '',
    response_status: 'answered' as ResponseStatus, display_order: '0',
  });
  const { mutation, error, setError } = useRowSave(candidateId, 'statements', row?.id ?? null, onSaved, onClose, 'Statement');

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      source_type: row?.source_type ?? 'questionnaire',
      source_name: row?.source_name ?? '',
      source_url: row?.source_url ?? '',
      source_date: dateValue(row?.source_date),
      question: row?.question ?? '',
      body: row?.body ?? '',
      response_status: row?.response_status ?? 'answered',
      display_order: String(row?.display_order ?? nextOrder),
    });
    setError(null);
  }, [isOpen, row, nextOrder, setError]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const answered = form.response_status === 'answered';
  const missing = !form.source_name.trim() || (answered && !form.body.trim());

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title={row ? 'Edit statement' : 'Add statement'}
      subtitle="The candidate's own words, verbatim, with the source they come from."
      size="lg"
      footer={<SaveFooter
        onCancel={onClose}
        disabled={missing}
        saving={mutation.isPending}
        onSave={() => mutation.mutate({
          source_type: form.source_type, source_name: form.source_name.trim(), source_url: blank(form.source_url),
          source_date: form.source_date || null, question: blank(form.question), body: answered ? form.body.trim() : null,
          response_status: form.response_status, display_order: Number(form.display_order) || 0,
        })}
      />}
    >
      <div className="space-y-4">
        <ErrorNote error={error} />
        <div className={fieldGrid}>
          <div>
            <label htmlFor="stmt-source-name" className={labelClasses}>Source name *</label>
            <input id="stmt-source-name" className="input" maxLength={255} value={form.source_name} onChange={set('source_name')} placeholder="e.g. Colorado Sun 2026 questionnaire" />
          </div>
          <div>
            <label htmlFor="stmt-source-type" className={labelClasses}>Source type</label>
            <select id="stmt-source-type" className="input" value={form.source_type} onChange={set('source_type')}>
              {Object.entries(STATEMENT_SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="stmt-source-url" className={labelClasses}>Source URL</label>
            <input id="stmt-source-url" type="url" className="input" value={form.source_url} onChange={set('source_url')} placeholder="https://" />
          </div>
          <div>
            <label htmlFor="stmt-source-date" className={labelClasses}>Source date</label>
            <input id="stmt-source-date" type="date" className="input" value={form.source_date} onChange={set('source_date')} />
          </div>
        </div>
        <div>
          <label htmlFor="stmt-question" className={labelClasses}>Question</label>
          <input id="stmt-question" className="input" value={form.question} onChange={set('question')} placeholder="The question as the source asked it (optional)" />
        </div>
        <div className={fieldGrid}>
          <div>
            <label htmlFor="stmt-status" className={labelClasses}>Response</label>
            <select id="stmt-status" className="input" value={form.response_status} onChange={set('response_status')}>
              {Object.entries(RESPONSE_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="stmt-order" className={labelClasses}>Display order</label>
            <input id="stmt-order" type="number" min={0} className="input" value={form.display_order} onChange={set('display_order')} />
          </div>
        </div>
        {answered ? (
          <div>
            <label htmlFor="stmt-body" className={labelClasses}>Answer (verbatim) *</label>
            <textarea id="stmt-body" rows={6} className="input" value={form.body} onChange={set('body')} />
          </div>
        ) : (
          <p className={hint}>A non-answer shows as &ldquo;{RESPONSE_STATUS_LABELS[form.response_status]}&rdquo; with the source date — give the date the source was checked.</p>
        )}
        <ReimportNote />
      </div>
    </AdminModal>
  );
};

/** The shared endorsement form (ENDORSEMENTS_PRD §5.5), saved through the profile-row route. */
export const EndorsementModal: React.FC<RowModalProps<CandidateEndorsement>> = ({ candidateId, isOpen, row, onClose, onSaved }) => {
  const { mutation, error, setError } = useRowSave(candidateId, 'endorsements', row?.id ?? null, onSaved, onClose, 'Endorsement');

  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen, setError]);

  return (
    <SharedEndorsementModal
      isOpen={isOpen}
      kind="candidate"
      row={row}
      saving={mutation.isPending}
      error={error}
      onSave={(input) => mutation.mutate(input)}
      onClose={onClose}
      note={<ReimportNote />}
    />
  );
};

const STANCE_OPTIONS: Stance[] = ['support', 'oppose', 'unclear', 'declined', 'no_position'];
const stanceOptionLabel = (s: Stance) => STANCE_LABELS[s] ?? 'Unclear';
const measureLabel = (m: { measure_number: string | null; popular_name: string | null; ballot_title: string | null }) =>
  [m.measure_number, m.popular_name ?? m.ballot_title].filter(Boolean).join(' — ') || 'Untitled measure';

export const PositionModal: React.FC<RowModalProps<CandidatePosition>> = ({ candidateId, isOpen, row, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ballot_initiative_id: '', stance: 'support' as Stance, quote: '', source_type: '', source_name: '', source_url: '', source_date: '',
  });
  const { mutation, error, setError } = useRowSave(candidateId, 'positions', row?.id ?? null, onSaved, onClose, 'Position');
  const options = useQuery({
    queryKey: ['admin-people', 'profile-measures', candidateId],
    queryFn: () => fetchMeasureOptions(candidateId),
    enabled: isOpen && !row,
  });

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      ballot_initiative_id: row?.ballot_initiative_id ?? '',
      stance: row?.stance ?? 'support',
      quote: row?.quote ?? '',
      source_type: row?.source_type ?? '',
      source_name: row?.source_name ?? '',
      source_url: row?.source_url ?? '',
      source_date: dateValue(row?.source_date) || today(),
    });
    setError(null);
  }, [isOpen, row, setError]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const noPosition = form.stance === 'no_position';
  const declined = form.stance === 'declined';
  const vote = form.source_type === 'referral_vote';
  const needsQuote = !noPosition && !declined && !vote;
  const missing = (!row && !form.ballot_initiative_id) || !form.source_date
    || (!noPosition && !form.source_name.trim())
    || (needsQuote && !form.quote.trim())
    || (vote && form.stance === 'unclear');
  const available = (options.data?.measures ?? []).filter((m) => !m.has_position);

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title={row ? `Edit position — ${measureLabel(row)}` : 'Add measure position'}
      subtitle="Never inferred: a taken stance needs the candidate's own words or their recorded vote."
      size="lg"
      footer={<SaveFooter
        onCancel={onClose}
        disabled={missing}
        saving={mutation.isPending}
        onSave={() => mutation.mutate({
          ballot_initiative_id: row?.ballot_initiative_id ?? form.ballot_initiative_id,
          stance: form.stance,
          quote: noPosition || declined ? null : blank(form.quote),
          source_type: noPosition ? null : (form.source_type || null),
          source_name: noPosition ? null : blank(form.source_name),
          source_url: noPosition ? null : blank(form.source_url),
          source_date: form.source_date,
        })}
      />}
    >
      <div className="space-y-4">
        <ErrorNote error={error} />
        {!row && (
          <div>
            <label htmlFor="pos-measure" className={labelClasses}>Measure *</label>
            <select id="pos-measure" className="input" value={form.ballot_initiative_id} onChange={set('ballot_initiative_id')} disabled={options.isLoading}>
              <option value="">{options.isLoading ? 'Loading measures…' : 'Choose a statewide measure'}</option>
              {available.map((m) => <option key={m.id} value={m.id}>{measureLabel(m)}</option>)}
            </select>
            <p className={hint}>
              Statewide measures on the {options.data?.cycle ?? 'same'} ballot without a row yet — edit an existing row instead of adding a second.
            </p>
          </div>
        )}
        <div className={fieldGrid}>
          <div>
            <label htmlFor="pos-stance" className={labelClasses}>Stance *</label>
            <select id="pos-stance" className="input" value={form.stance} onChange={set('stance')}>
              {STANCE_OPTIONS.map((s) => <option key={s} value={s}>{stanceOptionLabel(s)}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="pos-date" className={labelClasses}>{noPosition ? 'Roster checked on *' : 'Source date *'}</label>
            <input id="pos-date" type="date" className="input" value={form.source_date} onChange={set('source_date')} />
          </div>
        </div>
        {noPosition ? (
          <p className={hint}>&ldquo;No position found&rdquo; carries only the date the sources were checked — no quote or source.</p>
        ) : (
          <>
            <div className={fieldGrid}>
              <div>
                <label htmlFor="pos-source-name" className={labelClasses}>Source name *</label>
                <input id="pos-source-name" className="input" maxLength={255} value={form.source_name} onChange={set('source_name')} placeholder={declined ? 'The source that asked' : 'e.g. Campaign website'} />
              </div>
              <div>
                <label htmlFor="pos-source-type" className={labelClasses}>Source type</label>
                <select id="pos-source-type" className="input" value={form.source_type} onChange={set('source_type')}>
                  <option value="">—</option>
                  {Object.entries(POSITION_SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="pos-source-url" className={labelClasses}>Source URL</label>
              <input id="pos-source-url" type="url" className="input" value={form.source_url} onChange={set('source_url')} placeholder="https://" />
            </div>
            {!declined && (
              <div>
                <label htmlFor="pos-quote" className={labelClasses}>Quote (verbatim){needsQuote ? ' *' : ''}</label>
                <textarea id="pos-quote" rows={4} className="input" value={form.quote} onChange={set('quote')} />
                {vote && <p className={hint}>A recorded vote on referring the measure stands without a quote; it is support or oppose, never unclear.</p>}
              </div>
            )}
          </>
        )}
        <ReimportNote />
      </div>
    </AdminModal>
  );
};

// ── Delete ───────────────────────────────────────────────────────────────────

interface DeleteRowModalProps {
  candidateId: string;
  target: { kind: ProfileRowKind; id: string; label: string } | null;
  published: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteRowModal: React.FC<DeleteRowModalProps> = ({ candidateId, target, published, onClose, onDeleted }) => {
  const remove = useMutation({
    mutationFn: () => deleteProfileRow(candidateId, target!.kind, target!.id),
    onSuccess: () => {
      toast.success('Removed');
      onDeleted();
      onClose();
    },
    onError: (err: any) => toast.error(apiError(err, 'Could not remove the row')),
  });
  return (
    <AdminModal
      isOpen={target !== null}
      onClose={onClose}
      title="Remove this row?"
      size="sm"
      footer={<>
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="danger" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>Remove</Button>
      </>}
    >
      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
        <strong className="text-text-primary dark:text-text-dark-primary">{target?.label}</strong> is deleted
        {published ? ' and leaves every citizen surface immediately' : ''}. Re-importing the bundle would bring it back.
      </p>
    </AdminModal>
  );
};
